import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client';
import { configureClock, configureTimezone, type ClockPref, type TzPref } from '@/lib/datetime';
import { configureLocale } from '@/lib/i18n';

// Auth state. Hydrates from `/v1/api/me` - the same endpoint the /me
// page uses, so the nav, page header, and dev-tools all see one
// consistent identity. The previous sessionStorage mock has been
// removed: it was inventing "Jens Nylander / algot@jnylander.se" while
// the rest of the app loaded real Slack data for `jens / jens@tic.io`,
// which made every screen feel half-mocked. Real sign-in (cookie set
// by /v1/auth/slack/callback) flows through the same hydrate path.
//
// Sign-in is Slack-only: /v1/auth/slack/callback sets the sealed
// `hq-session` cookie. After that EVERY browser request - XHR, direct
// navigation (file download anchors), image src embeds, shareable links -
// carries identity via the cookie. There is no sessionStorage header path
// and no dev bypass anymore; both were sources of subtle bugs (and the dev
// bypass an auth hole), so the cookie is the single identity path.

export type Role = 'admin' | 'member';

export interface User {
  /// HQ-internal workspace_users.id.
  id: string;
  external_user_id: string;
  /// Caller's identity kind in THIS workspace ('slack' / 'teams' / 'manual').
  /// Per-session truth: gates whether a Slack-DM delivery target is meaningful
  /// (an email/manual identity has no Slack DM channel in this tenant).
  source_kind: string;
  /// True when THIS tenant has a live Slack install. Gates Slack-channel
  /// delivery targets (a workspace with no Slack must not be offered one).
  slack_connected: boolean;
  /// True when THIS tenant has a live Microsoft Teams install. The Teams mirror
  /// of `slack_connected`; together they let the app detect a workspace with no
  /// channel connected and nudge an admin to add one.
  teams_connected: boolean;
  display_name: string;
  email: string;
  locale: string;
  /// Web-UI clock preference: 'auto' follows the browser locale, '12h'/'24h'
  /// force the hour-cycle. Drives the central date/time formatter.
  time_format: ClockPref;
  /// Web-UI timezone preference: 'auto' (browser zone) or 'profile' (render in
  /// the user's profile `tz`).
  time_zone: TzPref;
  /// Profile IANA timezone (e.g. "Europe/Stockholm"), or null if unset. Used to
  /// resolve the 'profile' timezone preference and to label the /me control.
  tz: string | null;
  role: Role;
  /// Internal Trove URL (`/v1/api/artifacts/{uuid}/download`) for
  /// the user's profile image. `null` when no avatar has been
  /// synced into Trove yet -- the UI falls back to an initial.
  /// Backend never returns external URLs.
  avatar_url: string | null;
  tenant: {
    /// Tenant slug as carried in the `X-Hq-Workspace` header.
    slug: string;
    name: string;
  };
  /// Onboarding rollup surfaced on /me (P3). `completed` drives the
  /// router soft-gate that routes brand-new tenants into the wizard;
  /// `domain_confirmed` lets the wizard resume past the domain step;
  /// `unread_notifications` seeds the notification-bell badge before the
  /// first GET /v1/api/notifications resolves.
  onboarding?: {
    completed: boolean;
    domain_confirmed: boolean;
    business_domain: string | null;
    track: string;
    unread_notifications: number;
  };
}

/// Shape returned by GET /v1/api/me - wider than the User interface
/// because the page also wants persona + counts.
interface MeIdentity {
  user_id: string;
  external_user_id: string;
  tenant_slug: string;
  /// `slack` / `teams` / `manual` - the caller's identity kind in THIS
  /// workspace. Per-session: the same person can be a Slack identity in one
  /// tenant and an email/manual identity in another. Optional so older
  /// responses still type-check. Gates Slack-DM delivery targets.
  source_kind?: string;
  /// True when THIS tenant has a live Slack install. Optional for the same
  /// reason. Gates Slack-channel delivery targets.
  slack_connected?: boolean;
  /// True when THIS tenant has a live Microsoft Teams install. Optional so older
  /// responses still type-check; the Teams mirror of `slack_connected`.
  teams_connected?: boolean;
  source_workspace: string | null;
  display_name: string | null;
  real_name: string | null;
  email: string | null;
  locale: string | null;
  /// Workspace-wide default UI language (tenants.default_locale, default 'en').
  /// The fallback when the member has no `locale` of their own. Optional so an
  /// older response without it still type-checks.
  default_locale?: string | null;
  /// IANA timezone from the user's workspace profile (Slack/Teams), or null.
  tz?: string | null;
  /// 'auto' | '12h' | '24h' (migration 0169). Optional so older responses
  /// without it still type-check; absent => 'auto'.
  time_format?: string | null;
  /// 'auto' | 'profile' (migration 0170). Optional; absent => 'auto'.
  time_zone?: string | null;
  is_admin: boolean;
  /// Internal Trove URL (`/v1/api/artifacts/{uuid}/download`) for
  /// the user's profile image. Always internal, or null. Backend
  /// filters external URLs on the way out.
  avatar_url: string | null;
}
/// Onboarding rollup the /me response carries (P3) - a TOP-LEVEL field of the
/// response, NOT nested under `identity` (it mirrors the backend MeResp shape).
/// Optional so older responses without it still type-check. Timestamps are
/// presence-as-boolean: a non-null `completed_at` means onboarding is done; a
/// non-null `domain_confirmed_at` means the domain is set.
interface OnboardingRollup {
  completed_at: string | null;
  domain_confirmed_at: string | null;
  business_domain: string | null;
  track: string;
  unread_notifications: number;
}
interface MeResp {
  identity: MeIdentity;
  onboarding?: OnboardingRollup;
  out_of_credits?: boolean;
  is_operator?: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  /// True when the workspace is out of HQ credits (mirrors /me). Drives the
  /// hard-block: New-thread / New-ask actions + composer are disabled with an
  /// "upgrade to continue" prompt. The API also returns 402 on those paths -
  /// this flag is the proactive presentation; the 402 is the real boundary.
  const outOfCredits = ref(false);
  const hydrating = ref(false);
  // True after the first hydrate attempt finishes (success OR failure).
  // The router guard reads this to know whether `user.value === null`
  // means "definitely signed-out" (attempted=true) or "haven't asked
  // the server yet" (attempted=false, must await ensureHydrated).
  const attempted = ref(false);
  // Single in-flight hydrate promise: concurrent callers (initial
  // route guard + AppShell.onMounted) all wait on the same fetch
  // rather than firing /v1/api/me twice in quick succession.
  let inflight: Promise<void> | null = null;

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'admin');
  // True when this caller is an HQ platform operator (admin of an is_platform
  // tenant; today `tic`). Mirrors `/me.is_operator`. Gates the SPA's /operator
  // back-office nav + route; the real boundary is server-side on /v1/admin/*.
  const isOperator = ref(false);

  async function hydrate(): Promise<void> {
    hydrating.value = true;
    try {
      const me = await api.get<MeResp>('/v1/api/me');
      const i = me.identity;
      outOfCredits.value = me.out_of_credits ?? false;
      isOperator.value = me.is_operator ?? false;
      // Prefer `real_name` ("Jens Nylander") over the lowercase
      // `display_name` ("jens") so the chat header reads the way
      // Slack message metadata does. Falls back to display_name when
      // real_name isn't set, then to EMPTY - never the raw external id
      // (a UUID for email-origin users), which read as "Good afternoon,
      // f1bfcb9d-..." in the UI. Email users now get a real name from
      // the local part server-side (migration 0191); '' is just the
      // residual for nameless role mailboxes, and callers degrade
      // gracefully (the greeting drops the name entirely).
      user.value = {
        id: i.user_id,
        external_user_id: i.external_user_id,
        source_kind: i.source_kind ?? 'manual',
        slack_connected: i.slack_connected ?? false,
        teams_connected: i.teams_connected ?? false,
        display_name: i.real_name ?? i.display_name ?? '',
        email: i.email ?? '',
        locale: i.locale ?? 'en-US',
        time_format: (i.time_format as ClockPref) ?? 'auto',
        time_zone: (i.time_zone as TzPref) ?? 'auto',
        tz: i.tz ?? null,
        role: i.is_admin ? 'admin' : 'member',
        avatar_url: i.avatar_url ?? null,
        tenant: { slug: i.tenant_slug, name: i.source_workspace ?? i.tenant_slug },
        // Only set when present: `exactOptionalPropertyTypes` forbids
        // assigning `undefined` to an optional property. The /me block
        // carries timestamps; the router + wizard want booleans, so map
        // presence-as-boolean here.
        // Onboarding is a TOP-LEVEL field of /me (`me.onboarding`), not under
        // `identity`. Reading `i.onboarding` here used to leave it undefined,
        // which silently disabled the router's onboarding gate.
        ...(me.onboarding ? {
          onboarding: {
            completed: me.onboarding.completed_at != null,
            domain_confirmed: me.onboarding.domain_confirmed_at != null,
            business_domain: me.onboarding.business_domain,
            track: me.onboarding.track,
            unread_notifications: me.onboarding.unread_notifications,
          },
        } : {}),
      };
      // Push the saved clock + timezone preferences into the central date/time
      // formatter so every surface renders in the user's chosen hour-cycle and
      // zone. 'profile' resolves against the workspace profile tz.
      configureClock(i.time_format);
      configureTimezone({ mode: i.time_zone ?? null, profileTz: i.tz ?? null });
      // Locale chain: the member's own locale, else the workspace default
      // (tenants.default_locale), else English (the i18n resolver's hard default).
      configureLocale(i.locale ?? i.default_locale ?? null);
    } catch {
      // Not signed in. /signin handles routing.
      user.value = null;
      outOfCredits.value = false;
      isOperator.value = false;
    } finally {
      hydrating.value = false;
      attempted.value = true;
    }
  }

  /// Await an initial hydrate exactly once. The router guard calls
  /// this on the first navigation so it doesn't decide signed-out
  /// based on uninitialised state.
  async function ensureHydrated(): Promise<void> {
    if (attempted.value) return;
    if (!inflight) inflight = hydrate().finally(() => { inflight = null; });
    await inflight;
  }

  async function signOut(): Promise<void> {
    user.value = null;
    // Backend clears the sealed cookie. Errors are ignored: the user
    // is already locally signed out either way.
    try { await api.post('/v1/auth/signout', {}); } catch { /* ignore */ }
    // Hard navigation to the workspace-less sign-in. A router push would
    // resolve under the current `/w/<slug>/` history base and 404; a full
    // load also re-bases the router back to `/` for the signed-out session.
    window.location.assign('/signin');
  }

  return { user, outOfCredits, isOperator, hydrating, attempted, isAuthenticated, isAdmin,
           hydrate, ensureHydrated, signOut };
});
